import type { JSX } from 'react';
import { useMemo, useState, useSyncExternalStore } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Alert, Button as AntButton, Input, Skeleton } from 'antd';
import { SearchOutlined } from '@ant-design/icons';

import { friendlyError } from '../../lib/api';
import {
  OPENING_TASKS_KEYS,
  openingTasksApi,
  type OpeningPlanListItemDto,
  type OpeningPlanListStatus,
} from '../../lib/api/openingTasks';
import { Chip, DataTable, PageTitle, Tag, type DataColumn } from '../ds';
import { retryOpeningQuery } from '../opening-tasks/opening-query';
import './OpeningPlan.css';
import './OpeningManager.css';
import {
  cardHref,
  emptyListText,
  filterOpenings,
  LIST_STATUS_LABEL,
  LIST_STATUSES,
  leaseText,
  normalizeSearch,
  onStageParts,
  onStageText,
  pauseHint,
  paramsForListStatus,
  stageTasksRatio,
  stageTasksText,
} from './opening-manager-model';
import { CARD_STATUS, fmtDMY2, placeLabel } from './plan-model';

/** Телефон — как в OpeningPlan.css (@media max-width: 680px): вместо таблицы карточки. */
const PHONE_QUERY = '(max-width: 680px)';

function subscribePhone(onChange: () => void): () => void {
  const m = window.matchMedia(PHONE_QUERY);
  m.addEventListener('change', onChange);
  return () => m.removeEventListener('change', onChange);
}

function useIsPhone(): boolean {
  return useSyncExternalStore(
    subscribePhone,
    () => window.matchMedia(PHONE_QUERY).matches,
    () => false,
  );
}

const dash = <span className="fk-opm-muted">—</span>;

/** Колонки таблицы; клик по строке открывает карточку, а имя ИП — настоящая ссылка (клавиатура, «открыть в новой вкладке»). */
function buildColumns(status: OpeningPlanListStatus): DataColumn<OpeningPlanListItemDto>[] {
  return [
    {
      key: 'title',
      title: 'ИП',
      width: 230,
      fixed: 'left',
      value: (r) => r.title,
      render: (r) => (
        <span className="fk-opm-namecell">
          <Link to={cardHref(r.id, status)} className="fk-opm-name" onClick={(e) => e.stopPropagation()}>
            {r.title}
          </Link>
          {r.candidate ? (
            <Tag color="gold" borderless title="Договор франшизы ещё не подписан">
              кандидат
            </Tag>
          ) : null}
        </span>
      ),
    },
    {
      key: 'place',
      title: 'Город и адрес',
      width: 220,
      value: (r) => placeLabel(r.city, r.address),
      render: (r) => <span className="fk-opm-place">{placeLabel(r.city, r.address) || dash}</span>,
    },
    { key: 'stage', title: 'Этап', width: 130, value: (r) => r.stage.label },
    {
      key: 'days',
      title: 'На этапе',
      width: 130,
      value: (r) => r.daysOnStage,
      render: (r) => {
        const { days, norm } = onStageParts(r);
        return (
          <span className={r.overNorm ? 'fk-opm-bad' : undefined} title={r.overNorm ? 'Дольше нормы' : undefined}>
            {days}
            {norm ? <span className="fk-opm-sub">{norm}</span> : null}
          </span>
        );
      },
    },
    {
      key: 'tasks',
      title: 'Задачи этапа',
      width: 100,
      num: true,
      value: (r) => stageTasksRatio(r),
      render: (r) => stageTasksText(r) || dash,
    },
    {
      key: 'overdue',
      title: 'Просрочено',
      width: 120,
      num: true,
      value: (r) => r.overdueTasks,
      render: (r) =>
        r.overdueTasks > 0 ? (
          <Tag color="red" borderless>
            {r.overdueTasks}
          </Tag>
        ) : (
          dash
        ),
    },
    {
      key: 'soon',
      title: 'Скоро',
      width: 90,
      num: true,
      value: (r) => r.soonTasks,
      render: (r) =>
        r.soonTasks > 0 ? (
          <Tag color="orange" borderless>
            {r.soonTasks}
          </Tag>
        ) : (
          dash
        ),
    },
    {
      key: 'techOpen',
      title: 'Тех. открытие',
      width: 120,
      value: (r) => r.plannedOpenAt ?? null,
      render: (r) => fmtDMY2(r.plannedOpenAt) || dash,
    },
    {
      key: 'lease',
      title: 'Аренда',
      width: 160,
      value: (r) => r.leaseSignedAt ?? null,
      render: (r) => <span className={r.leaseSignedAt ? undefined : 'fk-opm-muted'}>{leaseText(r)}</span>,
    },
    {
      key: 'curator',
      title: 'Куратор',
      width: 140,
      value: (r) => r.curatorName ?? null,
      render: (r) => r.curatorName ?? dash,
    },
    {
      key: 'status',
      title: 'Статус',
      width: 110,
      value: (r) => CARD_STATUS[r.statusV2].label,
      render: (r) => (
        <Tag color={CARD_STATUS[r.statusV2].color} title={pauseHint(r)}>
          {CARD_STATUS[r.statusV2].label}
        </Tag>
      ),
    },
  ];
}

/** Карточка открытия на телефоне: всё важное в нескольких строках, нажатие открывает план. */
function ManagerCard({ item: r, status }: { item: OpeningPlanListItemDto; status: OpeningPlanListStatus }): JSX.Element {
  const st = CARD_STATUS[r.statusV2];
  const place = placeLabel(r.city, r.address);
  const tasks = stageTasksText(r);
  const hint = pauseHint(r);
  return (
    <Link to={cardHref(r.id, status)} className="fk-opm-card">
      <div className="fk-opm-card-top">
        <span className="fk-opm-card-name">{r.title}</span>
        <Tag color={st.color}>{st.label}</Tag>
      </div>
      {r.candidate || place ? (
        <div className="fk-opm-card-row">
          {r.candidate ? (
            <Tag color="gold" borderless>
              кандидат
            </Tag>
          ) : null}
          {place ? <span className="fk-opm-card-place">{place}</span> : null}
        </div>
      ) : null}
      <div className="fk-opm-card-row">
        <b>{r.stage.label}</b>
        <span className={r.overNorm ? 'fk-opm-bad' : undefined}>{onStageText(r)}</span>
      </div>
      <div className="fk-opm-card-row">
        {tasks ? <span>Задачи этапа {tasks}</span> : null}
        {r.overdueTasks > 0 ? (
          <Tag color="red" borderless>{`просрочено ${r.overdueTasks}`}</Tag>
        ) : null}
        {r.soonTasks > 0 ? (
          <Tag color="orange" borderless>{`скоро ${r.soonTasks}`}</Tag>
        ) : null}
      </div>
      <div className="fk-opm-card-meta">
        Тех. открытие: {fmtDMY2(r.plannedOpenAt) || '—'} · Аренда: {leaseText(r)}
      </div>
      <div className="fk-opm-card-meta">Куратор: {r.curatorName ?? '—'}</div>
      {hint ? <div className="fk-opm-card-meta">{hint}</div> : null}
    </Link>
  );
}

/**
 * Список открытий партнёров для менеджера УК (`GET /wiki/opening/plans`, право «Этапы открытия — просмотр»).
 * Фильтр «В работе / Открытые / Все» живёт в адресе (`?status=`, «В работе» — без параметра), поиск по ИП, городу и
 * адресу — на клиенте. Нажатие на строку (карточку) открывает план: `?card=<id>`, фильтр в адресе остаётся.
 */
export function OpeningManagerList({ status }: { status: OpeningPlanListStatus }): JSX.Element {
  const navigate = useNavigate();
  const [, setSp] = useSearchParams();
  const isPhone = useIsPhone();
  const [query, setQuery] = useState('');

  const list = useQuery({
    queryKey: OPENING_TASKS_KEYS.plans(status),
    queryFn: () => openingTasksApi.plans(status),
    // 4xx (нет права, режим «от лица») — сразу к ошибке; один повтор только на сбой сервера или сети.
    retry: retryOpeningQuery,
    staleTime: 30_000,
    // Смена чипа не мигает пустым экраном: прежний список остаётся, пока грузится новый.
    placeholderData: keepPreviousData,
  });

  const all = list.data?.items;
  const shown = useMemo(() => filterOpenings(all ?? [], query), [all, query]);
  const columns = useMemo(() => buildColumns(status), [status]);
  const stale = list.isPlaceholderData;
  const searching = normalizeSearch(query) !== '';

  let body: JSX.Element;
  if (list.isLoading) {
    body = <Skeleton active paragraph={{ rows: 6 }} />;
  } else if (list.error && !all) {
    body = (
      <Alert
        type="error"
        showIcon
        message="Не удалось загрузить открытия"
        description={friendlyError(list.error)}
        action={
          <AntButton size="small" onClick={() => void list.refetch()} loading={list.isFetching}>
            Повторить
          </AntButton>
        }
      />
    );
  } else if (shown.length === 0) {
    body = (
      <section className="fk-op-card" aria-label="Открытия партнёров">
        <div className="fk-ot-empty">{emptyListText(status, query)}</div>
      </section>
    );
  } else if (isPhone) {
    body = (
      <div className="fk-opm-cards" data-stale={stale} aria-label="Открытия партнёров">
        {shown.map((r) => (
          <ManagerCard key={r.id} item={r} status={status} />
        ))}
      </div>
    );
  } else {
    body = (
      <div className="fk-opm-table">
        <DataTable<OpeningPlanListItemDto>
          columns={columns}
          rows={shown}
          rowKey={(r) => r.id}
          loading={stale && list.isFetching}
          onRowClick={(r) => navigate(cardHref(r.id, status))}
        />
      </div>
    );
  }

  return (
    <div className="fk-op">
      <PageTitle
        title="Открытие точки"
        sub="Открытия партнёров — выберите точку, чтобы посмотреть план, сроки и задачи"
        style={{ marginBottom: 0 }}
      />
      <div className="fk-opm-bar">
        <div className="fk-opm-chips" role="group" aria-label="Какие открытия показать">
          {LIST_STATUSES.map((k) => (
            <Chip key={k} active={status === k} onClick={() => setSp(paramsForListStatus(k), { replace: true })}>
              {LIST_STATUS_LABEL[k]}
            </Chip>
          ))}
        </div>
        <Input
          className="fk-opm-search"
          allowClear
          prefix={<SearchOutlined aria-hidden="true" />}
          placeholder="ИП, город или адрес"
          aria-label="Поиск по ИП, городу и адресу"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        {all && searching ? <span className="fk-opm-count">{`Найдено: ${shown.length} из ${all.length}`}</span> : null}
      </div>
      {body}
    </div>
  );
}
